import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import api from '../../services/api';
import transportService from '../../services/transportService';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { OrderRecord } from '../../types/orders';

interface CreateTransportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrderIds: number[];
  selectedOrders?: OrderRecord[]; // Przekazywane z Transports.tsx
  onSuccess?: () => void;
}

interface Forwarder {
  name: string;
  email?: string;
  phone?: string;
}

interface Driver {
  name: string;
  email?: string;
  phone?: string;
}

interface Carrier {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  nip?: string;
  forwarders?: Forwarder[];
  drivers?: Driver[];
}

interface EmailRecipient {
  name?: string;
  email: string;
}

interface CarrierInvitation {
  carrier_id: number;
  carrier_name?: string;
  recipient_emails: {
    main_email?: string;
    forwarder_emails: EmailRecipient[];
    driver_emails: EmailRecipient[];
  };
}

export const CreateTransportModal = ({ isOpen, onClose, selectedOrderIds, selectedOrders: selectedOrdersProp = [], onSuccess }: CreateTransportModalProps) => {
  const { t } = useTranslation();
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [carrierInvitations, setCarrierInvitations] = useState<Map<number, CarrierInvitation>>(new Map());

  const [transportForm, setTransportForm] = useState<any>({
    readiness_date: '',
    delivery_address: '',
    client_name: '',
    client_surname: '',
    client_company: '',
    client_nip: '',
    client_email: '',
    notes: '',
    packages: [{ length_mm: undefined, width_mm: undefined, height_mm: undefined, weight_kg: undefined }]
  });

  useEffect(() => {
    if (isOpen) {
      loadCarriers();
    }
  }, [isOpen]);

  const loadCarriers = async () => {
    try {
      const resp = await api.get('/transport/carriers/list?include_details=true');
      setCarriers(resp.data || []);
    } catch (e) {
      console.error('Failed to load carriers', e);
    }
  };

  const toggleRow = (rowIndex: number) => {
    setExpandedRow(prev => prev === rowIndex ? null : rowIndex);
  };

  // Helper: split carriers into rows of 2
  const chunkCarriers = (arr: Carrier[], size: number = 2): Carrier[][] => {
    const chunks: Carrier[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const toggleEmail = (carrierId: number, email: string, type: 'main' | 'forwarder' | 'driver', name?: string) => {
    setCarrierInvitations(prev => {
      const newMap = new Map(prev);
      const existingInvitation = newMap.get(carrierId);
      const carrier = carriers.find(c => c.id === carrierId);

      // Create new invitation object (immutable)
      let newRecipientEmails = {
        main_email: existingInvitation?.recipient_emails.main_email,
        forwarder_emails: existingInvitation?.recipient_emails.forwarder_emails || [],
        driver_emails: existingInvitation?.recipient_emails.driver_emails || []
      };

      if (type === 'main') {
        newRecipientEmails.main_email = newRecipientEmails.main_email ? undefined : email;
      } else if (type === 'forwarder') {
        // Immutable array operations - now store objects with name
        const existingIndex = newRecipientEmails.forwarder_emails.findIndex((item: any) =>
          typeof item === 'string' ? item === email : item.email === email
        );
        if (existingIndex >= 0) {
          newRecipientEmails.forwarder_emails = newRecipientEmails.forwarder_emails.filter((_: any, i: number) => i !== existingIndex);
        } else {
          newRecipientEmails.forwarder_emails = [...newRecipientEmails.forwarder_emails, { name, email }];
        }
      } else if (type === 'driver') {
        // Immutable array operations - now store objects with name
        const existingIndex = newRecipientEmails.driver_emails.findIndex((item: any) =>
          typeof item === 'string' ? item === email : item.email === email
        );
        if (existingIndex >= 0) {
          newRecipientEmails.driver_emails = newRecipientEmails.driver_emails.filter((_: any, i: number) => i !== existingIndex);
        } else {
          newRecipientEmails.driver_emails = [...newRecipientEmails.driver_emails, { name, email }];
        }
      }

      // If no emails selected, remove invitation
      if (!newRecipientEmails.main_email &&
          newRecipientEmails.forwarder_emails.length === 0 &&
          newRecipientEmails.driver_emails.length === 0) {
        newMap.delete(carrierId);
      } else {
        newMap.set(carrierId, {
          carrier_id: carrierId,
          carrier_name: carrier?.name || `Przewoźnik #${carrierId}`,
          recipient_emails: newRecipientEmails
        });
      }

      return newMap;
    });
  };

  const isEmailSelected = (carrierId: number, email: string, type: 'main' | 'forwarder' | 'driver'): boolean => {
    const invitation = carrierInvitations.get(carrierId);
    if (!invitation) return false;

    if (type === 'main') return invitation.recipient_emails.main_email === email;
    if (type === 'forwarder') {
      return invitation.recipient_emails.forwarder_emails.some((item: any) =>
        typeof item === 'string' ? item === email : item.email === email
      );
    }
    if (type === 'driver') {
      return invitation.recipient_emails.driver_emails.some((item: any) =>
        typeof item === 'string' ? item === email : item.email === email
      );
    }
    return false;
  };

  const getSelectedEmailsCount = (carrierId: number): number => {
    const invitation = carrierInvitations.get(carrierId);
    if (!invitation) return 0;
    let count = 0;
    if (invitation.recipient_emails.main_email) count++;
    count += invitation.recipient_emails.forwarder_emails.length;
    count += invitation.recipient_emails.driver_emails.length;
    return count;
  };

  const handleSave = async () => {
    if (selectedOrderIds.length === 0) {
      toast.error('Nie wybrano żadnych zamówień');
      return;
    }

    if (carrierInvitations.size === 0) {
      toast.error('Nie wybrano żadnych przewoźników ani adresów email');
      return;
    }

    try {
      const payload = {
        order_ids: selectedOrderIds,
        carrier_invitations: Array.from(carrierInvitations.values()),
        readiness_date: transportForm.readiness_date || undefined,
        delivery_address: transportForm.delivery_address || undefined,
        client_name: transportForm.client_name || undefined,
        client_surname: transportForm.client_surname || undefined,
        client_company: transportForm.client_company || undefined,
        client_nip: transportForm.client_nip || undefined,
        client_email: transportForm.client_email || undefined,
        notes: transportForm.notes || undefined,
        packages: transportForm.packages.map((p: any) => ({
          length_mm: Number(p.length_mm) || undefined,
          width_mm: Number(p.width_mm) || undefined,
          height_mm: Number(p.height_mm) || undefined,
          weight_kg: Number(p.weight_kg) || undefined,
        }))
      };

      await transportService.create(payload);
      toast.success('Zlecenie transportowe zostało utworzone');

      // Reset form
      setTransportForm({
        readiness_date: '',
        delivery_address: '',
        client_name: '',
        client_surname: '',
        client_company: '',
        client_nip: '',
        client_email: '',
        notes: '',
        packages: [{ length_mm: undefined, width_mm: undefined, height_mm: undefined, weight_kg: undefined }]
      });
      setCarrierInvitations(new Map());
      setExpandedRow(null);

      onClose();
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error('Nie udało się utworzyć zlecenia transportowego');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Utwórz zlecenie transportowe (${selectedOrderIds.length} zamówień)`}
      size="xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Data gotowości */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('transport.fields.readiness', 'Data gotowości')}
          </label>
          <input
            type="date"
            className="w-full border rounded px-3 py-2"
            value={transportForm.readiness_date}
            onChange={(e) => setTransportForm((p: any) => ({ ...p, readiness_date: e.target.value }))}
          />
        </div>

        {/* Spacer */}
        <div></div>

        {/* Dane klienta - Sekcja */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Dane klienta</h3>
        </div>

        {/* Imię */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Imię
          </label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={transportForm.client_name}
            onChange={(e) => setTransportForm((p: any) => ({ ...p, client_name: e.target.value }))}
            placeholder="Jan"
          />
        </div>

        {/* Nazwisko */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nazwisko
          </label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={transportForm.client_surname}
            onChange={(e) => setTransportForm((p: any) => ({ ...p, client_surname: e.target.value }))}
            placeholder="Kowalski"
          />
        </div>

        {/* Firma */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Firma
          </label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={transportForm.client_company}
            onChange={(e) => setTransportForm((p: any) => ({ ...p, client_company: e.target.value }))}
            placeholder="Nazwa firmy"
          />
        </div>

        {/* NIP */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            NIP
          </label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={transportForm.client_nip}
            onChange={(e) => setTransportForm((p: any) => ({ ...p, client_nip: e.target.value }))}
            placeholder="123-456-78-90"
          />
        </div>

        {/* E-mail */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            E-mail
          </label>
          <input
            type="email"
            className="w-full border rounded px-3 py-2"
            value={transportForm.client_email}
            onChange={(e) => setTransportForm((p: any) => ({ ...p, client_email: e.target.value }))}
            placeholder="klient@example.com"
          />
        </div>

        {/* Adres */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Adres
          </label>
          <textarea
            className="w-full border rounded px-3 py-2"
            rows={2}
            value={transportForm.delivery_address}
            onChange={(e) => setTransportForm((p: any) => ({ ...p, delivery_address: e.target.value }))}
            placeholder="Ulica, numer, kod pocztowy, miasto"
          />
        </div>

        {/* Sekcja z wybranymi zamówieniami */}
        {selectedOrdersProp.length > 0 && (
          <div className="md:col-span-2 mt-4 mb-2">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                Wybrane zamówienia do transportu
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {selectedOrdersProp.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white border border-blue-300 rounded-lg p-3 shadow-sm"
                  >
                    <div className="text-xs font-semibold text-blue-700 mb-1">
                      {order.order_nr || `#${order.id}`}
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {order.ref_name || 'Brak nazwy'}
                    </div>
                    {order.order_date && (
                      <div className="text-xs text-gray-500 mt-1">
                        {order.order_date}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sekcja Przewoźnicy */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Transport</h3>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('transport.fields.carriers', 'Wybierz przewoźników i osoby do wysyłki')}
          </label>
          <div className="space-y-3">
            {chunkCarriers(carriers).map((row, rowIndex) => {
              const isRowExpanded = expandedRow === rowIndex;

              return (
                <div key={rowIndex} className="border rounded-lg overflow-hidden bg-white shadow-sm">
                  {/* Row Header with Expand Button + Carrier Tiles */}
                  <div className="flex items-start gap-2 p-3">
                    {/* Expand Arrow Button */}
                    <button
                      onClick={() => toggleRow(rowIndex)}
                      className="flex-shrink-0 p-2 hover:bg-gray-100 rounded transition-colors"
                    >
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform ${isRowExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Carrier Tiles */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {row.map(carrier => {
                        const selectedCount = getSelectedEmailsCount(carrier.id);
                        return (
                          <div
                            key={carrier.id}
                            className={`border rounded-lg p-3 ${
                              selectedCount > 0 ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
                            }`}
                          >
                            <div className="font-semibold text-gray-900">{carrier.name}</div>
                            {selectedCount > 0 && (
                              <div className="text-xs text-blue-600 mt-1">
                                ✓ {selectedCount} {selectedCount === 1 ? 'osoba' : selectedCount < 5 ? 'osoby' : 'osób'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Expandable Content for WHOLE ROW */}
                  {isRowExpanded && (
                    <div className="px-3 pb-3 border-t bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
                        {/* Loop through each carrier in the row */}
                        {row.map(carrier => (
                          <div key={carrier.id} className="space-y-3">
                            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                              {carrier.name}
                            </div>

                            {/* Main Email */}
                            {carrier.email && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">Email główny</div>
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={isEmailSelected(carrier.id, carrier.email, 'main')}
                                    onChange={() => toggleEmail(carrier.id, carrier.email!, 'main', carrier.name)}
                                    className="rounded"
                                  />
                                  <span className="font-medium">{carrier.email}</span>
                                </label>
                              </div>
                            )}

                            {/* Forwarders */}
                            {carrier.forwarders && carrier.forwarders.length > 0 && (
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                <div className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">Spedytorzy</div>
                                <div className="space-y-2">
                                  {carrier.forwarders.map((fwd, idx) => (
                                    fwd.email && (
                                      <label key={idx} className="block cursor-pointer">
                                        <div className="flex items-center gap-2 text-sm">
                                          <input
                                            type="checkbox"
                                            checked={isEmailSelected(carrier.id, fwd.email, 'forwarder')}
                                            onChange={() => toggleEmail(carrier.id, fwd.email!, 'forwarder', fwd.name)}
                                            className="rounded"
                                          />
                                          <span className="font-medium">{fwd.name}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 ml-6 break-all">{fwd.email}</div>
                                      </label>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Drivers */}
                            {carrier.drivers && carrier.drivers.length > 0 && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">Kierowcy</div>
                                <div className="space-y-2">
                                  {carrier.drivers.map((drv, idx) => (
                                    drv.email && (
                                      <label key={idx} className="block cursor-pointer">
                                        <div className="flex items-center gap-2 text-sm">
                                          <input
                                            type="checkbox"
                                            checked={isEmailSelected(carrier.id, drv.email, 'driver')}
                                            onChange={() => toggleEmail(carrier.id, drv.email!, 'driver', drv.name)}
                                            className="rounded"
                                          />
                                          <span className="font-medium">{drv.name}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 ml-6 break-all">{drv.email}</div>
                                      </label>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}

                            {!carrier.email && (!carrier.forwarders || carrier.forwarders.length === 0) && (!carrier.drivers || carrier.drivers.length === 0) && (
                              <div className="text-xs text-gray-500 italic">Brak dostępnych adresów email</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {carriers.length === 0 && (
              <div className="text-sm text-gray-500 col-span-2 text-center py-4">Brak zdefiniowanych przewoźników</div>
            )}
          </div>
        </div>

        {/* Live Preview - Selected Recipients */}
        {carrierInvitations.size > 0 && (
          <div className="md:col-span-2 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="text-sm font-semibold text-green-900 mb-3">
              Osoby do których zostanie wysłane zapytanie ofertowe:
            </h4>
            <div className="space-y-2">
              {Array.from(carrierInvitations.entries()).map(([carrierId, invitation]) => {
                const carrier = carriers.find(c => c.id === carrierId);
                if (!carrier) return null;

                return (
                  <div key={carrierId} className="text-sm">
                    <div className="font-semibold text-green-900">{carrier.name}:</div>
                    <ul className="list-disc list-inside pl-4 text-green-800">
                      {invitation.recipient_emails.main_email && (
                        <li>{invitation.recipient_emails.main_email} (Email główny)</li>
                      )}
                      {invitation.recipient_emails.forwarder_emails.map((item: any, idx: number) => (
                        <li key={`fwd-${idx}`}>
                          {typeof item === 'string' ? item : `${item.name} - ${item.email}`} (Spedytor)
                        </li>
                      ))}
                      {invitation.recipient_emails.driver_emails.map((item: any, idx: number) => (
                        <li key={`drv-${idx}`}>
                          {typeof item === 'string' ? item : `${item.name} - ${item.email}`} (Kierowca)
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('transport.fields.packages', 'Wymiar ładowny')}
          </label>
          <div className="space-y-2">
            {transportForm.packages.map((p: any, idx: number) => (
              <div key={idx} className="grid grid-cols-4 gap-2">
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  className="border rounded px-2 py-1"
                  value={p.length_mm ?? ''}
                  onChange={(e) => setTransportForm((pf: any) => {
                    const arr = [...pf.packages];
                    arr[idx] = { ...arr[idx], length_mm: e.target.value };
                    return { ...pf, packages: arr };
                  })}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  className="border rounded px-2 py-1"
                  value={p.width_mm ?? ''}
                  onChange={(e) => setTransportForm((pf: any) => {
                    const arr = [...pf.packages];
                    arr[idx] = { ...arr[idx], width_mm: e.target.value };
                    return { ...pf, packages: arr };
                  })}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  className="border rounded px-2 py-1"
                  value={p.height_mm ?? ''}
                  onChange={(e) => setTransportForm((pf: any) => {
                    const arr = [...pf.packages];
                    arr[idx] = { ...arr[idx], height_mm: e.target.value };
                    return { ...pf, packages: arr };
                  })}
                />
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="0"
                  className="border rounded px-2 py-1"
                  value={p.weight_kg ?? ''}
                  onChange={(e) => setTransportForm((pf: any) => {
                    const arr = [...pf.packages];
                    arr[idx] = { ...arr[idx], weight_kg: e.target.value };
                    return { ...pf, packages: arr };
                  })}
                />
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => setTransportForm((pf: any) => ({
                ...pf,
                packages: [...pf.packages, { length_mm: undefined, width_mm: undefined, height_mm: undefined, weight_kg: undefined }]
              }))}
            >
              {t('transport.actions.addPackage', 'Dodaj paczkę')}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel', 'Anuluj')}
        </Button>
        <Button onClick={handleSave}>
          {t('common.save', 'Zapisz')}
        </Button>
      </div>
    </Modal>
  );
};
